import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { PageLayout } from "../../../../common/layout";
import { useRootStore } from "../../../../store/contexts";
import { MusicalWorkCreate } from "./MusicalWorkCreate";
import { MusicalWorkCreateStore } from "../../store/MusicalWorkCreateStore";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { useStore } from "../../../../hooks";
import { CallbackDataParams } from "../../../../store/types";

export const MusicalWorkCreatePage = observer(() => {
  const rootStore = useRootStore();
  const { router, langStore } = rootStore;
  const location = useLocation();

  const duplicateFrom = router.params?.duplicateFrom as string;
  const path = duplicateFrom
    ? `/musicalWork/create/${duplicateFrom}`
    : "/musicalWork/create";

  // Get callback params from navigation state (when opened via "Add new")
  const navState = location.state as {
    callbackParams?: CallbackDataParams;
    initialDisplayId?: string;
  } | null;
  const callbackParams = navState?.callbackParams;
  const initialDisplayId = navState?.initialDisplayId;

  const store = useStore(
    path,
    () => new MusicalWorkCreateStore(rootStore),
    duplicateFrom
      ? { duplicateFromId: duplicateFrom }
      : initialDisplayId
      ? { initialDisplayId }
      : undefined
  );

  // Setup callback data if this was opened via "Add new"
  useEffect(() => {
    if (callbackParams?.storeId && !store.callbackReturnPath) {
      store.callbackReturnPath = callbackParams.storeId;
      store.setCallbackParams(callbackParams);
    }
  }, [callbackParams, store]);

  return (
    <div>
      <PageLayout
        navigationElements={{
          links: store.navigationLinks,
          onNavigatedTo: (id) => {
            store.setCardState(id, true);
          },
        }}
        secondaryToolbox={
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => store.saveNewMusicalWork()}
            saveDisabled={store.isProcessing}
            closeForm={() => store.cancelNewMusicalWork()}
          />
        }
      >
        <MusicalWorkCreate musicalWorkCreateStore={store} showToolbox={false} />
      </PageLayout>
      <Dialog
        open={store.validationErrorsDialogOpen}
        onClose={() => store.closeValidationErrorsDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{langStore.validationErrorsTitle}</DialogTitle>
        <DialogContent>
          <Box>
            {Object.keys(store.errors).map((key) => {
              const error = store.errors[key as keyof typeof store.errors];
              if (!error) return null;
              return (
                <Typography
                  key={key}
                  color="error"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  • {error.fieldLabel}: {error.message}
                </Typography>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => store.closeValidationErrorsDialog()}
            color="primary"
          >
            {langStore.closeCommand}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});
